// utils.jsx
const subtle = window.crypto.subtle;

function base64ToUint8Array(base64) {
  if (!base64) {
    throw new Error("Base64 string is undefined or empty");
  }
  base64 = base64.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) {
    base64 += "=";
  }

  const raw = atob(base64);
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

export async function generateNewKey() {
  return subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export async function encryptSharedKey(shareKeyCrypto, publicKeyPem) {
  const pemContents = publicKeyPem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s/g, "");
  const publicKeyCrypto = await window.crypto.subtle.importKey(
    "spki",
    base64ToArrayBuffer(pemContents),
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["encrypt"]
  );
  const rawShareKey = await window.crypto.subtle.exportKey("raw", shareKeyCrypto);
  const encrypted = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKeyCrypto,
    rawShareKey
  );
  return arrayBufferToBase64(encrypted);
}

export async function decryptSharedKey(encryptedShareKeyBase64, privateKeyPem) {
  const pemContents = privateKeyPem
    .replace(/-----BEGIN PRIVATE KEY-----/, "")
    .replace(/-----END PRIVATE KEY-----/, "")
    .replace(/\s/g, "");
  const privateKeyCrypto = await window.crypto.subtle.importKey(
    "pkcs8",
    base64ToArrayBuffer(pemContents),
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["decrypt"]
  );
  const decryptedRawKey = await window.crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    privateKeyCrypto,
    base64ToArrayBuffer(encryptedShareKeyBase64)
  );
  return await window.crypto.subtle.importKey(
    "raw",
    decryptedRawKey,
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function deriveMasterKey(masterPassword, salt) {
  const encoder = new TextEncoder();
  const keyMaterial = await subtle.importKey(
    "raw",
    encoder.encode(masterPassword),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return await subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode(salt),
      iterations: 250000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function encryptVaultKey(vaultKey, masterKey) {
  const rawVaultKey = await subtle.exportKey("raw", vaultKey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  const encryptedBuffer = await subtle.encrypt(
    { name: "AES-GCM", iv },
    masterKey,
    rawVaultKey
  );

  return {
    encryptedVaultKey: btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer))),
    vaultKeyIV: Array.from(iv)
  };
}

export async function decryptVaultKey(encryptedVaultKeyBase64, vaultKeyIV, masterKey) {
  const encryptedBytes = base64ToUint8Array(encryptedVaultKeyBase64);
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(vaultKeyIV) },
    masterKey,
    encryptedBytes
  );
  return await crypto.subtle.importKey(
    "raw",
    decryptedBuffer,
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function encryptGroupKey(groupKey, vaultKey) {
  const rawGroupKey = await subtle.exportKey("raw", groupKey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await subtle.encrypt(
    { name: "AES-GCM", iv },
    vaultKey,
    rawGroupKey
  );
  return {
    encryptedGroupKey: btoa(
      String.fromCharCode(...new Uint8Array(encryptedBuffer))
    ),
    groupKeyIV: Array.from(iv)
  };
}

export async function decryptGroupKey(encryptedGroupKeyBase64, groupKeyIV, vaultKey) {
  const encryptedBytes = base64ToUint8Array(encryptedGroupKeyBase64);
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(groupKeyIV) },
    vaultKey,
    encryptedBytes
  );
  return crypto.subtle.importKey(
    "raw",
    decryptedBuffer,
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function encryptItemKey(itemKey, vaultKey) {
  const rawItemKey = await subtle.exportKey("raw", itemKey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await subtle.encrypt(
    { name: "AES-GCM", iv },
    vaultKey,
    rawItemKey
  );
  return {
    encryptedItemKey: btoa(
      String.fromCharCode(...new Uint8Array(encryptedBuffer))
    ),
    itemKeyIV: Array.from(iv)
  };
}

export async function decryptItemKey(encryptedItemKeyBase64, itemKeyIV, vaultKey) {
  const encryptedBytes = base64ToUint8Array(encryptedItemKeyBase64);
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(itemKeyIV) },
    vaultKey,
    encryptedBytes
  );
  return crypto.subtle.importKey(
    "raw",
    decryptedBuffer,
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function encryptPayload(payload, vaultKey) {
  const encoder = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const payloadStr = JSON.stringify(payload);
  const encryptedBuffer = await subtle.encrypt(
    { name: "AES-GCM", iv },
    vaultKey,
    encoder.encode(payloadStr)
  );
  return {
    encryptedData: btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer))),
    encryptedDataIV: Array.from(iv)
  };
}

export async function decryptPayload(encryptedData, encryptedDataIV, vaultKey) {
  const encryptedBytes = base64ToUint8Array(encryptedData);
  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: new Uint8Array(encryptedDataIV)
    },
    vaultKey,
    encryptedBytes
  );

  return JSON.parse(new TextDecoder().decode(decryptedBuffer));
}

function base64ToArrayBufferPrivateKey(base64) {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function arrayBufferToBase64PrivateKey(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function formatPEM(base64String, type = "PRIVATE KEY") {
  const chunkSize = 64;
  const chunks = base64String.match(new RegExp(`.{1,${chunkSize}}`, "g"));
  return `-----BEGIN ${type}-----\n${chunks.join("\n")}\n-----END ${type}-----`;
}

export async function decryptPrivateKey(encryptedPrivateKeyBase64, privateKeyIVArray, vaultKeyCryptoKey) {
  const encryptedBuffer = base64ToArrayBufferPrivateKey(encryptedPrivateKeyBase64);
  const iv = new Uint8Array(privateKeyIVArray);
  const rawPrivateKeyBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    vaultKeyCryptoKey,
    encryptedBuffer
  );
  const base64PrivateKey = arrayBufferToBase64PrivateKey(rawPrivateKeyBuffer);
  const pem = formatPEM(base64PrivateKey, "PRIVATE KEY");
  return pem;
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function getDomainName(hostname) {
  const parts = hostname.split(".");
  const filtered = parts.filter(p => !["www", "mail", "m"].includes(p.toLowerCase()));
  return filtered[0] || parts[0];
}

async function getFinalUrl(url) {
  try {
    const response = await fetch(url, { redirect: "follow" });
    return response.url;
  } catch {
    return url;
  }
}

export async function getWebsiteMeta(url) {
  try {
    const response = await fetch(url);
    const html = await response.text();
    let titleMatch = html.match(/<title>(.*?)<\/title>/i);
    let title = titleMatch ? titleMatch[1].trim() : null;
    if (!title) {
      const hostname = new URL(url).hostname;
      const domainName = getDomainName(hostname);
      title = capitalize(domainName);
    }
    let favicon = null;
    const linkIconRegex = /<link[^>]+rel=["'](?:shortcut icon|icon|apple-touch-icon)["'][^>]*>/gi;
    const iconTag = html.match(linkIconRegex);
    if (iconTag && iconTag[0]) {
      const hrefMatch = iconTag[0].match(/href=["']([^"']+)["']/i);
      if (hrefMatch) {
        favicon = hrefMatch[1];
        if (favicon.startsWith("/")) {
          favicon = new URL(favicon, url).href;
        }
        favicon = await getFinalUrl(favicon);
      }
    }
    if (!favicon) {
      favicon = await getFinalUrl(new URL("/favicon.ico", url).href);
    }
    return { title, favicon };
  } catch (err) {
    console.error("Error fetching metadata:", err);
    const hostname = new URL(url).hostname;
    const domainName = getDomainName(hostname);
    return {
      title: capitalize(domainName),
      favicon: await getFinalUrl(new URL("/favicon.ico", url).href),
    };
  }
}

export const fetchIFSCDetails = async (ifscCode) => {
  try {
    const url = `https://ifsc.razorpay.com/${ifscCode}`;
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data;
  } catch (err) {
    return null;
  }
};

export function maskNumber(number) {
  const str = number.toString();
  const last4 = str.slice(-4);
  return "XXXX XXXX XXXX " + last4;
}

export function maskBankAccount(accountNumber) {
  const str = accountNumber.toString();
  const last4 = str.slice(-4);
  const maskedLength = str.length - 4;
  const masked = "X".repeat(maskedLength);
  const grouped = masked.match(/.{1,4}/g)?.join(" ") || "";
  return grouped + " " + last4;
}

export function getPasswordFromItem(item) {
  const field = item?.itemInfo?.find(f => f.name === "PASSWORD");
  return typeof field?.value === "string" ? field.value : "";
}

export function getKeyFromItem(item, key) {
  const field = item?.itemInfo?.find(f => f.name === key);
  return field?.value
}

export function evaluatePassword(password) {
  const pwd = typeof password === "string" ? password : "";

  const issues = [];
  const recommendations = [];

  if (!pwd) {
    return {
      score: 0,
      strength: "Weak",
      riskLevel: "High",
      issues: ["Missing password"],
      recommendations: ["Set a password with at least 12 characters"]
    };
  }

  const commonPasswords = [
    "password",
    "123456",
    "12345678",
    "admin",
    "qwerty"
  ];

  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);
  const hasNumber = /[0-9]/.test(pwd);
  const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
  const repeatRegex = /(.)\1{2,}/;
  const sequenceRegex = /(012|123|234|345|456|567|678|789|abc|bcd|cde)/i;

  // ---------- COMMON PASSWORD (HARD FAIL, BUT EDUCATIONAL) ----------
  const isCommon = commonPasswords.includes(pwd.toLowerCase());
  if (isCommon) {
    issues.push("Common password");
    recommendations.push(
      "Avoid commonly used passwords",
      "Use a unique passphrase that you have never used before"
    );
  }

  // ---------- CHECKS FOR GUIDANCE ----------
  if (!hasUpper) {
    issues.push("No uppercase letter");
    recommendations.push("Add at least one uppercase letter (A–Z)");
  }

  if (!hasLower) {
    issues.push("No lowercase letter");
    recommendations.push("Add at least one lowercase letter (a–z)");
  }

  if (!hasNumber) {
    issues.push("No number");
    recommendations.push("Add at least one number (0–9)");
  }

  if (!hasSpecial) {
    issues.push("No special character");
    recommendations.push("Add at least one special character (!@#$)");
  }

  if (repeatRegex.test(pwd)) {
    issues.push("Repeated characters");
    recommendations.push("Avoid repeating the same character multiple times");
  }

  if (sequenceRegex.test(pwd)) {
    issues.push("Sequential pattern");
    recommendations.push("Avoid sequential patterns like 123 or abc");
  }

  if (pwd.length < 12) {
    issues.push("Password too short");
    recommendations.push("Use at least 12 characters for better security");
  }

  // ---------- SCORE ----------
  let score = 0;

  if (!isCommon) {
    if (pwd.length >= 8) score += 10;
    if (pwd.length >= 12) score += 10;
    if (pwd.length >= 16) score += 5;

    if (hasUpper) score += 8;
    if (hasLower) score += 8;
    if (hasNumber) score += 7;
    if (hasSpecial) score += 7;

    const uniqueChars = new Set(pwd).size;
    score += Math.min(15, uniqueChars);

    if (repeatRegex.test(pwd)) score -= 15;
    if (sequenceRegex.test(pwd)) score -= 15;
  }

  score = Math.max(0, Math.min(100, score));

  const strength =
    score >= 80 ? "Strong" :
      score >= 50 ? "Medium" :
        "Weak";

  return {
    score,
    strength,
    riskLevel:
      strength === "Strong" ? "Low" :
        strength === "Medium" ? "Medium" :
          "High",
    issues: [...new Set(issues)],
    recommendations: [...new Set(recommendations)]
  };
}

export function calculateReuseMap(items = []) {
  const map = {};

  items.forEach(item => {
    const pwd = getPasswordFromItem(item);
    if (!pwd) return;

    map[pwd] = (map[pwd] || 0) + 1;
  });

  return map;
}
